<template>
  <div id="error-detail" class="s-base">
    <v-card class="s-panel" max-width="1000">
      <v-card-title class="s-title">障害詳細画面</v-card-title>
      <v-container>
        <v-row>
          <v-col>
            <v-card>
              <v-card-title>エラーログ(編集不可)</v-card-title>
              <v-textarea
                v-model="errorDataInput.errorDetail"
                class="px-5 pb-5"
                outlined
                auto-grow
                no-resize
                readonly
              ></v-textarea>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card>
              <v-card-title>ステータス</v-card-title>
              <v-radio-group
                v-model="errorDataInput.statusCode"
                row
                class="pl-5 pb-5"
              >
                <v-radio
                  v-for="errorStatus in errorStatusList"
                  :key="errorStatus.code"
                  :label="errorStatus.name"
                  :value="errorStatus.code"
                ></v-radio>
              </v-radio-group>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card>
              <v-card-title>備考</v-card-title>
              <v-textarea
                v-model="errorDataInput.memo"
                counter="100"
                maxlength="100"
                rows="1"
                class="px-5 pb-5"
                auto-grow
                no-resize
              ></v-textarea>
            </v-card>
          </v-col>
        </v-row>
        <v-row no-gutters justify="space-between">
          <v-col cols="auto" class="px-0 py-5">
            <v-btn width="100" color="primary" @click="goToPreviousPage()"
              >戻る</v-btn
            >
          </v-col>
          <v-col cols="auto" class="px-0 py-5">
            <v-btn width="100" color="secondary" @click="editErrorData()"
              >登録</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <message-dialog
      :messageDialogFlag.sync="dialog.message"
      :dialogText="messageDialogText"
    />
    <request-error-dialog
      :requestErrorDialogFlag.sync="dialog.requestError"
      :httpStatus="httpStatus"
      :message="requestErrorDialogMessage"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { NETWORK_ERROR } from "../router";
import errorModule, { ErrorData } from "../store/ErrorModule";
import errorStatusModule, { ErrorStatusList } from "../store/ErrorStatusModule";
import MessageDialog from "./MessageDialog.vue";
import RequestErrorDialog from "./RequestErrorDialog.vue";

@Component({
  components: {
    "message-dialog": MessageDialog,
    "request-error-dialog": RequestErrorDialog,
  },
})
export default class ErrorDetail extends Vue {
  /** 画面遷移時にデータ読み込みのエラー判定 */
  mountedOk = false;

  /** ダイアログの表示フラグ */
  dialog = {
    message: false,
    requestError: false,
  };

  /** httpステータスコード */
  httpStatus = 500;

  /** リクエストエラーダイアログのテキスト */
  requestErrorDialogMessage = "登録に失敗しました。";

  /** メッセージダイアログのテキスト */
  messageDialogText = {
    title: "登録完了",
    message: "登録が完了しました。",
  };

  /** 障害データの編集入力値 */
  errorDataInput: ErrorData = {
    errorId: -1,
    errorDetail: "",
    statusCode: -1,
    memo: "",
  };

  /**
   * 障害ステータス種別リスト
   * @returns {ErrorStatusList} 障害ステータス種別リスト
   */
  get errorStatusList(): ErrorStatusList {
    return errorStatusModule.errorStatusList;
  }

  /**
   * ひとつ前のページに遷移する。
   */
  goToPreviousPage(): void {
    this.$router.back();
  }

  /**
   * DBの障害データの情報を編集する。
   */
  async editErrorData(): Promise<void> {
    const errorDataInput = {
      errorId: this.errorDataInput.errorId,
      statusCode: this.errorDataInput.statusCode,
      memo: this.errorDataInput.memo,
    };
    let result = false;
    try {
      result = await errorModule.editErrorData(errorDataInput);
    } catch (error) {
      this.requestErrorDialogMessage = "登録に失敗しました。";
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else if (error.message === "404") {
          // 404 Not Found
          this.httpStatus = 404;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
    if (result === true) {
      await this.loadAllDataToStore();
      this.copyStoreData();
      this.dialog.message = true;
    }
  }

  /**
   * Storeのデータをコピーする。
   */
  copyStoreData(): void {
    // Storeのデータを書き換えないように、一度Json型に変換。
    this.errorDataInput = JSON.parse(JSON.stringify(errorModule.errorData));
  }

  /**
   * 各データをDBからStoreに取り込む
   */
  async loadAllDataToStore(): Promise<void> {
    try {
      await errorModule.getErrorData(parseInt(this.$route.params.id));
      await errorStatusModule.getErrorStatusList();
      this.mountedOk = true;
    } catch (error) {
      this.requestErrorDialogMessage = "";
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else if (error.message === "404") {
          // 404 Not Found
          this.httpStatus = 404;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
  }

  /**
   * 障害詳細画面に遷移した時に実行
   */
  async mounted() {
    await this.loadAllDataToStore();
    if (this.mountedOk === true) {
      this.copyStoreData();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/scss/common.scss";
.v-text-field--outlined {
  ::v-deep fieldset {
    border-color: black;
  }
}
</style>
